import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Circle,
  CornerDownRight,
  GripVertical,
  Shuffle,
  Square,
  Star,
} from "lucide-react-native";
import { FC, useState } from "react";
import { ScrollView } from "react-native";

import { Box, Center, HStack, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { AdhocQuestion, Question, Rule } from "@/types/resources";
import { RuleOperator } from "@/types/utils";
import CarryForwardBadge from "./rules/CarryForwardBadge";

interface QuestionContentProps {
  allQuestions?: Question[];
  question: AdhocQuestion | Question;
}

const MATRIX_PAGE_SIZE = 10;

const QuestionContent: FC<QuestionContentProps> = ({
  allQuestions = [],
  question,
}) => {
  const { i18n } = useLocalization();
  const [matrixPage, setMatrixPage] = useState(0);
  const rules = "rules" in question ? question.rules : [];

  // Get option-specific rule (for single choice)
  const getOptionRule = (optionIndex: number): Rule | undefined => {
    return rules.find(
      (r) => r.option_index === optionIndex && r.source_question_id === null
    );
  };

  const getRuleTargetDisplay = (rule: Rule): string => {
    if (rule.target_question_id === null) {
      return i18n.t("questions.endSurvey");
    }
    const targetOrder = allQuestions.find(
      (q) => q.id === rule.target_question_id
    )?.order;
    return i18n.t("questions.skipToQuestion", { number: targetOrder });
  };

  /* Option-based skip rule read-out: the operator (is / is not / contains /
     does not contain) matters as much as the target, so surface it alongside
     the arrow rather than showing the same thing for eq/in and neq/notin. */
  const optionRuleIndicator = (rule: Rule) => {
    const negated =
      rule.operator === RuleOperator.NotEqual ||
      rule.operator === RuleOperator.NotIn;
    return (
      <HStack className="items-center gap-1">
        <Text className="text-xs text-typography-500">
          {negated
            ? i18n.t("questions.ifNotSelected")
            : i18n.t("questions.ifSelected")}
        </Text>
        <CornerDownRight className="text-primary-600" size={14} />
        <Text className="text-xs text-primary-600">
          {getRuleTargetDisplay(rule)}
        </Text>
      </HStack>
    );
  };

  /* Red call-to-action shown when a question has no options/lines yet —
     mirrors the section card's empty-state hint (warning icon + red text). */
  const emptyHint = (message: string) => (
    <HStack className="items-center gap-1 pl-4 pt-3 pb-1">
      <AlertCircle color="rgb(var(--color-error-500))" size={12} />
      <Text className="text-xs font-medium text-error-500">{message}</Text>
    </HStack>
  );

  const carryForward =
    "carry_forward" in question ? question.carry_forward : null;
  const hasMedia = (question.media_urls?.length ?? 0) > 0;
  /* Blank (empty-string) options/lines aren't valid content, so an all-blank
     list is treated as "none provided". */
  const hasContent = (values: string[]) =>
    values.some((value) => (value ?? "").trim() !== "");

  const carryForwardBadge = carryForward ? (
    <CarryForwardBadge
      allQuestions={allQuestions}
      carryForward={carryForward}
    />
  ) : null;

  /* A carried dimension is filled at run time from a prior answer, so it can't
     be drawn as a grid. We instead stack the two dimensions (rows / columns)
     as labelled lists, swapping the carried one for its carry-forward banner
     so authors can tell at a glance what's ported and from where. */
  const renderCarriedMatrix = (CellIcon: typeof Circle) => {
    const optionsCarried = carryForward?.target === "options";
    const rowsList = hasContent(question.lines) ? (
      <VStack className="gap-1.5">
        {question.lines.map((line, index) => (
          <HStack key={`row-${index}`} className="items-center gap-2">
            <GripVertical color="rgb(var(--color-typography-300))" size={14} />
            <Text className="text-xs text-typography-700">{line}</Text>
          </HStack>
        ))}
      </VStack>
    ) : (
      emptyHint(i18n.t("questions.noLinesProvided"))
    );
    const columnsList = hasContent(question.options) ? (
      <VStack className="gap-1.5">
        {question.options.map((option, index) => (
          <HStack key={`col-${index}`} className="items-center gap-2">
            <CellIcon className="text-typography-400" size={14} />
            <Text className="text-xs text-typography-700">{option}</Text>
          </HStack>
        ))}
      </VStack>
    ) : (
      emptyHint(i18n.t("questions.noOptionsProvided"))
    );
    return (
      <VStack className="gap-4 pl-4 pt-4 pb-2">
        <VStack className="gap-2">
          <Text className="text-xs font-semibold text-typography-500">
            {i18n.t("questions.matrix.rows")}
          </Text>
          {optionsCarried ? rowsList : carryForwardBadge}
        </VStack>
        <VStack className="gap-2">
          <Text className="text-xs font-semibold text-typography-500">
            {i18n.t("questions.matrix.columns")}
          </Text>
          {optionsCarried ? carryForwardBadge : columnsList}
        </VStack>
      </VStack>
    );
  };

  switch (question.type) {
    case "category_assignment": {
      const { assignment } = question as Question;

      if (!hasContent(question.options))
        return emptyHint(i18n.t("questions.noOptionsProvided"));

      /* Every track needs a destination once a later question exists to route
         to. We surface the gap as a red CTA (consistent with the empty hints)
         rather than blocking save. */
      const order = "order" in question ? question.order : 0;
      const hasDownstream = allQuestions.some((q) => q.order > order);
      const routedTracks = new Set(
        rules
          .filter(
            (r) => r.source_question_id === null && r.option_index !== null
          )
          .map((r) => r.option_index)
      );
      const missingRoutes =
        hasDownstream && question.options.some((_, i) => !routedTracks.has(i));

      return (
        <VStack className="gap-2 pl-4 pt-4 pb-2">
          {missingRoutes && emptyHint(i18n.t("questions.noTrackDestinations"))}
          {assignment && (
            <HStack className="items-center gap-1.5 bg-primary-50 dark:bg-primary-900/20 rounded-md px-2.5 py-1.5">
              <Text className="text-sm font-medium text-primary-700 dark:text-primary-300">
                {i18n.t("questions.categoryAssignment.method", {
                  method:
                    assignment.method === "least_fill"
                      ? i18n.t("questions.categoryAssignment.leastFill")
                      : i18n.t("questions.categoryAssignment.random"),
                })}
              </Text>
            </HStack>
          )}
          {question.options.map((option, index) => {
            const rule = getOptionRule(index);
            const mappings =
              assignment?.mappings.filter((m) => m.option_index === index) ??
              [];

            return (
              <VStack key={index} className="gap-1">
                <HStack className="items-center gap-3 py-1">
                  <Shuffle className="text-typography-400" size={14} />
                  <Text className="text-xs text-typography-900 flex-1">
                    {option}
                  </Text>
                  {rule && (
                    <HStack className="items-center gap-1">
                      <CornerDownRight className="text-primary-600" size={14} />
                      <Text className="text-xs text-primary-600">
                        {getRuleTargetDisplay(rule)}
                      </Text>
                    </HStack>
                  )}
                </HStack>
                {mappings.length > 0 && (
                  <VStack className="gap-0.5 pl-7">
                    {mappings.map((m, mi) => {
                      const sourceQ = allQuestions.find(
                        (q) => q.id === m.source_question_id
                      );
                      const sourceLabel =
                        sourceQ?.options?.[m.source_option_index] ||
                        i18n.t("questions.option", {
                          number: m.source_option_index + 1,
                        });
                      const sourceNumber = sourceQ?.order ?? "?";
                      const params = {
                        number: sourceNumber,
                        option: sourceLabel,
                      };
                      // Operator drives the phrasing; legacy mappings (no
                      // operator) are always a positive match, keyed on the
                      // source type for membership vs equality.
                      const negated =
                        m.operator === RuleOperator.NotEqual ||
                        m.operator === RuleOperator.NotIn;
                      const membership =
                        m.operator === RuleOperator.In ||
                        m.operator === RuleOperator.NotIn ||
                        (m.operator === undefined &&
                          sourceQ?.type === "multiple_choice");
                      return (
                        <Text key={mi} className="text-xs text-typography-500">
                          {membership
                            ? negated
                              ? i18n.t(
                                  "questions.categoryAssignment.eligibilityDoesNotContain",
                                  params
                                )
                              : i18n.t(
                                  "questions.categoryAssignment.eligibilityContains",
                                  params
                                )
                            : negated
                              ? i18n.t(
                                  "questions.categoryAssignment.eligibilityIsNot",
                                  params
                                )
                              : i18n.t(
                                  "questions.categoryAssignment.eligibilityIs",
                                  params
                                )}
                        </Text>
                      );
                    })}
                  </VStack>
                )}
              </VStack>
            );
          })}
        </VStack>
      );
    }

    case "single_choice": {
      /* Options carried from a prior question are resolved at run time, so we
         show the carry-forward banner in place of the (empty) option list. */
      if (carryForward?.target === "options")
        return <Box className="pl-4 pt-4 pb-2">{carryForwardBadge}</Box>;
      /* Image-mode questions carry their options as media (rendered above), so
         a blank `options` list is only "empty" when there are no images. */
      if (!hasContent(question.options))
        return hasMedia
          ? null
          : emptyHint(i18n.t("questions.noOptionsProvided"));
      const optionCount = question.options.length;
      const totalPages = Math.ceil(optionCount / MATRIX_PAGE_SIZE);
      const visibleOptions = question.options.slice(
        matrixPage * MATRIX_PAGE_SIZE,
        (matrixPage + 1) * MATRIX_PAGE_SIZE
      );
      return (
        <VStack className="gap-2 pt-4 pb-2">
          <VStack className="gap-2 pl-4">
            {visibleOptions.map((option, localIndex) => {
              const rule = getOptionRule(
                matrixPage * MATRIX_PAGE_SIZE + localIndex
              );
              return (
                <HStack
                  key={matrixPage * MATRIX_PAGE_SIZE + localIndex}
                  className="items-center gap-3 py-1"
                >
                  <Circle className="text-typography-400" size={14} />
                  <Text className="text-xs text-typography-900 flex-1">
                    {option}
                  </Text>
                  {rule && optionRuleIndicator(rule)}
                </HStack>
              );
            })}
          </VStack>
          {optionCount > MATRIX_PAGE_SIZE && (
            <HStack className="items-center justify-end gap-2 pr-1">
              <Text className="text-xs text-typography-400">
                {i18n.t("questions.matrix.optionsRange", {
                  from: matrixPage * MATRIX_PAGE_SIZE + 1,
                  to: Math.min(
                    (matrixPage + 1) * MATRIX_PAGE_SIZE,
                    optionCount
                  ),
                  total: optionCount,
                })}
              </Text>
              <Pressable
                className="p-1"
                disabled={matrixPage === 0}
                onPress={() => setMatrixPage((p) => p - 1)}
              >
                <ChevronLeft
                  className={
                    matrixPage === 0
                      ? "text-typography-300"
                      : "text-typography-600"
                  }
                  size={16}
                />
              </Pressable>
              <Pressable
                className="p-1"
                disabled={matrixPage === totalPages - 1}
                onPress={() => setMatrixPage((p) => p + 1)}
              >
                <ChevronRight
                  className={
                    matrixPage === totalPages - 1
                      ? "text-typography-300"
                      : "text-typography-600"
                  }
                  size={16}
                />
              </Pressable>
            </HStack>
          )}
        </VStack>
      );
    }

    case "multiple_choice": {
      if (carryForward?.target === "options")
        return <Box className="pl-4 pt-4 pb-2">{carryForwardBadge}</Box>;
      if (!hasContent(question.options))
        return hasMedia
          ? null
          : emptyHint(i18n.t("questions.noOptionsProvided"));
      const optionCount = question.options.length;
      const totalPages = Math.ceil(optionCount / MATRIX_PAGE_SIZE);
      const visibleOptions = question.options.slice(
        matrixPage * MATRIX_PAGE_SIZE,
        (matrixPage + 1) * MATRIX_PAGE_SIZE
      );
      return (
        <VStack className="gap-2 pt-4 pb-2">
          <VStack className="gap-2 pl-4">
            {visibleOptions.map((option, localIndex) => {
              const rule = getOptionRule(
                matrixPage * MATRIX_PAGE_SIZE + localIndex
              );
              return (
                <HStack
                  key={matrixPage * MATRIX_PAGE_SIZE + localIndex}
                  className="items-center gap-3 py-1"
                >
                  <Square className="text-typography-400" size={14} />
                  <Text className="text-xs text-typography-900 flex-1">
                    {option}
                  </Text>
                  {rule && optionRuleIndicator(rule)}
                </HStack>
              );
            })}
          </VStack>
          {optionCount > MATRIX_PAGE_SIZE && (
            <HStack className="items-center justify-end gap-2 pr-1">
              <Text className="text-xs text-typography-400">
                {i18n.t("questions.matrix.optionsRange", {
                  from: matrixPage * MATRIX_PAGE_SIZE + 1,
                  to: Math.min(
                    (matrixPage + 1) * MATRIX_PAGE_SIZE,
                    optionCount
                  ),
                  total: optionCount,
                })}
              </Text>
              <Pressable
                className="p-1"
                disabled={matrixPage === 0}
                onPress={() => setMatrixPage((p) => p - 1)}
              >
                <ChevronLeft
                  className={
                    matrixPage === 0
                      ? "text-typography-300"
                      : "text-typography-600"
                  }
                  size={16}
                />
              </Pressable>
              <Pressable
                className="p-1"
                disabled={matrixPage === totalPages - 1}
                onPress={() => setMatrixPage((p) => p + 1)}
              >
                <ChevronRight
                  className={
                    matrixPage === totalPages - 1
                      ? "text-typography-300"
                      : "text-typography-600"
                  }
                  size={16}
                />
              </Pressable>
            </HStack>
          )}
        </VStack>
      );
    }

    case "open_ended":
      return (
        <Box className="pl-4 pt-4 pb-2">
          <Box className="border border-dashed border-outline-200 rounded-xl bg-background-50/50 p-4 min-h-[120px]">
            <Text className="text-xs text-typography-400 italic">
              {i18n.t("questions.longFormTextAnswer")}
            </Text>
          </Box>
        </Box>
      );

    case "pricing":
      return (
        <Box className="pl-4 pt-4 pb-2">
          <Box className="border border-dashed border-outline-200 rounded-xl bg-background-50/50 px-4 py-3 max-w-xs">
            <Text className="text-xs text-typography-400 italic">
              {i18n.t("questions.pricingPlaceholder")}
            </Text>
          </Box>
        </Box>
      );

    case "rating_scale":
      return (
        <HStack className="gap-3 pl-4 justify-center pt-4 pb-2">
          {question.options.map((value) => (
            <VStack key={value} className="items-center gap-1">
              <Text className="text-xs text-typography-700">{value}</Text>
              <Star className="text-outline-300" size={14} />
            </VStack>
          ))}
        </HStack>
      );

    case "linear_scale": {
      const startLabel = question.options[0] || "";
      const endLabel = question.options[question.options.length - 1] || "";
      return (
        <HStack className="items-end gap-6 pl-4 pt-4 pb-2">
          <Text className="flex-1 text-right text-xs text-typography-600">
            {startLabel}
          </Text>
          <HStack className="items-start gap-3">
            {question.options.map((_, index) => (
              <VStack key={index} className="items-center gap-1">
                <Text className="text-xs text-typography-700">{index + 1}</Text>
                <HStack className="items-center justify-center w-5 h-5 rounded-full border border-outline-300" />
              </VStack>
            ))}
          </HStack>
          <Text className="flex-1 text-left text-xs text-typography-600">
            {endLabel}
          </Text>
        </HStack>
      );
    }

    case "nps": {
      const npsScale = Array.from({ length: 11 }, (_, i) => i);
      return (
        <HStack className="items-start justify-center gap-3 pl-4 pt-4 pb-2">
          <VStack className="items-center gap-1">
            <Text className="text-xs text-transparent">0</Text>
            <Text className="text-xs text-typography-600 h-5 leading-5">
              {i18n.t("questions.npsExtremelyUnlikely")}
            </Text>
          </VStack>
          {npsScale.map((value) => (
            <VStack key={value} className="items-center gap-1">
              <Text className="text-xs text-typography-700">{value}</Text>
              <HStack className="items-center justify-center w-5 h-5 rounded-full border border-outline-300" />
            </VStack>
          ))}
          <VStack className="items-center gap-1">
            <Text className="text-xs text-transparent">0</Text>
            <Text className="text-xs text-typography-600 h-5 leading-5">
              {i18n.t("questions.npsExtremelyLikely")}
            </Text>
          </VStack>
        </HStack>
      );
    }

    case "ranking": {
      if (!hasContent(question.lines))
        return emptyHint(i18n.t("questions.noLinesProvided"));
      return (
        <VStack className="gap-2 pl-3 pt-4 pb-2">
          {question.lines.map((line, index) => (
            <HStack
              key={`rank-${index}`}
              className="items-center gap-2 bg-background-50 rounded-lg px-3 py-2"
            >
              <GripVertical
                color="rgb(var(--color-typography-300))"
                size={14}
              />
              <Text className="text-xs text-typography-700">{line}</Text>
            </HStack>
          ))}
        </VStack>
      );
    }

    case "single_select_matrix": {
      // A carried dimension can't be drawn in the grid, so fall back to the
      // stacked rows/columns layout with the banner in the carried slot.
      if (carryForward) return renderCarriedMatrix(Circle);
      if (!hasContent(question.lines))
        return emptyHint(i18n.t("questions.noLinesProvided"));
      if (!hasContent(question.options))
        return emptyHint(i18n.t("questions.noOptionsProvided"));
      const totalPages = Math.ceil(question.lines.length / MATRIX_PAGE_SIZE);
      const visibleLines = question.lines.slice(
        matrixPage * MATRIX_PAGE_SIZE,
        (matrixPage + 1) * MATRIX_PAGE_SIZE
      );
      return (
        <VStack className="gap-2 pt-4 pb-2">
          <HStack className="gap-6 items-start" style={{ paddingLeft: 16 }}>
            <VStack className="gap-4">
              <Box className="h-8" />
              {visibleLines.map((line, rowIndex) => (
                <Box
                  key={`label-${rowIndex}`}
                  className="h-8 w-40 justify-center"
                >
                  <Text
                    className="text-xs text-typography-700"
                    style={{
                      overflow: "hidden",
                      // @ts-expect-error - web-only CSS properties
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {line}
                  </Text>
                </Box>
              ))}
            </VStack>
            <ScrollView horizontal showsHorizontalScrollIndicator>
              <HStack className="gap-6 items-start">
                {question.options.map((option, colIndex) => (
                  <VStack key={`col-${colIndex}`} className="gap-4">
                    <Center className="h-8 w-40">
                      <Text
                        className="text-xs text-typography-600 text-center"
                        style={{
                          overflow: "hidden",
                          // @ts-expect-error - web-only CSS properties
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {option}
                      </Text>
                    </Center>
                    {visibleLines.map((_, rowIndex) => (
                      <Center
                        key={`cell-${rowIndex}-${colIndex}`}
                        className="h-8 w-40"
                      >
                        <Circle className="text-outline-300" size={14} />
                      </Center>
                    ))}
                  </VStack>
                ))}
              </HStack>
            </ScrollView>
          </HStack>
          {totalPages > 1 && (
            <HStack className="items-center justify-end gap-2 pr-1">
              <Text className="text-xs text-typography-400">
                {i18n.t("questions.matrix.rowsRange", {
                  from: matrixPage * MATRIX_PAGE_SIZE + 1,
                  to: Math.min(
                    (matrixPage + 1) * MATRIX_PAGE_SIZE,
                    question.lines.length
                  ),
                  total: question.lines.length,
                })}
              </Text>
              <Pressable
                className="p-1"
                disabled={matrixPage === 0}
                onPress={() => setMatrixPage((p) => p - 1)}
              >
                <ChevronLeft
                  className={
                    matrixPage === 0
                      ? "text-typography-300"
                      : "text-typography-600"
                  }
                  size={16}
                />
              </Pressable>
              <Pressable
                className="p-1"
                disabled={matrixPage === totalPages - 1}
                onPress={() => setMatrixPage((p) => p + 1)}
              >
                <ChevronRight
                  className={
                    matrixPage === totalPages - 1
                      ? "text-typography-300"
                      : "text-typography-600"
                  }
                  size={16}
                />
              </Pressable>
            </HStack>
          )}
        </VStack>
      );
    }

    case "multi_select_matrix": {
      // A carried dimension can't be drawn in the grid, so fall back to the
      // stacked rows/columns layout with the banner in the carried slot.
      if (carryForward) return renderCarriedMatrix(Square);
      if (!hasContent(question.lines))
        return emptyHint(i18n.t("questions.noLinesProvided"));
      if (!hasContent(question.options))
        return emptyHint(i18n.t("questions.noOptionsProvided"));
      const totalPages = Math.ceil(question.lines.length / MATRIX_PAGE_SIZE);
      const visibleLines = question.lines.slice(
        matrixPage * MATRIX_PAGE_SIZE,
        (matrixPage + 1) * MATRIX_PAGE_SIZE
      );
      return (
        <VStack className="gap-2 pt-4 pb-2">
          <HStack className="gap-6 items-start" style={{ paddingLeft: 16 }}>
            <VStack className="gap-4">
              <Box className="h-8" />
              {visibleLines.map((line, rowIndex) => (
                <Box
                  key={`label-${rowIndex}`}
                  className="h-8 w-40 justify-center"
                >
                  <Text
                    className="text-xs text-typography-700"
                    style={{
                      overflow: "hidden",
                      // @ts-expect-error - web-only CSS properties
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {line}
                  </Text>
                </Box>
              ))}
            </VStack>
            <ScrollView horizontal showsHorizontalScrollIndicator>
              <HStack className="gap-6 items-start">
                {question.options.map((option, colIndex) => (
                  <VStack key={`col-${colIndex}`} className="gap-4">
                    <Center className="h-8 w-40">
                      <Text
                        className="text-xs text-typography-600 text-center"
                        style={{
                          overflow: "hidden",
                          // @ts-expect-error - web-only CSS properties
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {option}
                      </Text>
                    </Center>
                    {visibleLines.map((_, rowIndex) => (
                      <Center
                        key={`cell-${rowIndex}-${colIndex}`}
                        className="h-8 w-40"
                      >
                        <Square className="text-outline-300" size={14} />
                      </Center>
                    ))}
                  </VStack>
                ))}
              </HStack>
            </ScrollView>
          </HStack>
          {totalPages > 1 && (
            <HStack className="items-center justify-end gap-2 pr-1">
              <Text className="text-xs text-typography-400">
                {i18n.t("questions.matrix.rowsRange", {
                  from: matrixPage * MATRIX_PAGE_SIZE + 1,
                  to: Math.min(
                    (matrixPage + 1) * MATRIX_PAGE_SIZE,
                    question.lines.length
                  ),
                  total: question.lines.length,
                })}
              </Text>
              <Pressable
                className="p-1"
                disabled={matrixPage === 0}
                onPress={() => setMatrixPage((p) => p - 1)}
              >
                <ChevronLeft
                  className={
                    matrixPage === 0
                      ? "text-typography-300"
                      : "text-typography-600"
                  }
                  size={16}
                />
              </Pressable>
              <Pressable
                className="p-1"
                disabled={matrixPage === totalPages - 1}
                onPress={() => setMatrixPage((p) => p + 1)}
              >
                <ChevronRight
                  className={
                    matrixPage === totalPages - 1
                      ? "text-typography-300"
                      : "text-typography-600"
                  }
                  size={16}
                />
              </Pressable>
            </HStack>
          )}
        </VStack>
      );
    }

    default:
      return null;
  }
};

export default QuestionContent;
